import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, Shield, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL, DIGIO_ENVIRONMENT } from '@/config';
import {
  useDigioSdk,
  getDigioConstructor,
  resolveDigioToken,
  removeOrphanedDigioOverlays,
  fitDigioOverlayToViewport,
  type DigioInstance,
} from '@/hooks/useDigioSdk';

interface CaprionAadhaarVerificationProps {
  value?: any;
  onChange?: (data: any) => void;
  onDone?: () => void;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  isBuilderMode?: boolean;
}

const CaprionAadhaarVerification: React.FC<CaprionAadhaarVerificationProps> = ({
  value,
  onChange,
  onDone,
  registerSubmit,
  isBuilderMode = false,
}) => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [verificationData, setVerificationData] = useState<any>(null);
  const [pollingActive, setPollingActive] = useState(false);
  const [faceMatchValidated, setFaceMatchValidated] = useState(false);
  const [faceMatchLoading, setFaceMatchLoading] = useState(false);
  const [faceMatchError, setFaceMatchError] = useState('');

  // Prefilled silently from API — not shown to user
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');

  // Shared Digio SDK loader (idempotent, shared with NACH/UPI)
  const { loaded: sdkLoaded, error: sdkLoadError } = useDigioSdk();

  // Refs for Digio instance and overlay cleanup
  const digioRef = useRef<DigioInstance | null>(null);
  const overlayCleanupRef = useRef<(() => void) | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasLoadedUserRef = useRef(false); // Prevent duplicate user details fetch
  const hasCheckedVerificationRef = useRef(false); // Prevent duplicate verification check

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (digioRef.current) {
        try { digioRef.current.cancel(); } catch { /* ignore */ }
        digioRef.current = null;
      }
      if (overlayCleanupRef.current) {
        overlayCleanupRef.current();
        overlayCleanupRef.current = null;
      }
      removeOrphanedDigioOverlays();
    };
  }, []);

  // Silently fetch user details on mount (name + phone for Digio SDK, not shown in UI)
  useEffect(() => {
    // Prevent duplicate calls from React StrictMode or re-renders
    if (hasLoadedUserRef.current) return;
    hasLoadedUserRef.current = true;

    const fetchUserDetails = async () => {
      try {
        const ckycResponse = await axios.get(`${API_BASE_URL}/api/caprion/user-ckyc-details/`);
        if (ckycResponse.data?.success) {
          const data = ckycResponse.data.data;
          setCustomerName(data.name || '');
          setCustomerPhone(data.mobile || '');
        }
      } catch {
        try {
          const sessionResponse = await axios.get(`${API_BASE_URL}/api/check_session/`);
          if (sessionResponse.data?.authenticated) {
            setCustomerPhone(sessionResponse.data.username || '');
          }
        } catch { /* silent */ }
      }
    };
    fetchUserDetails();
  }, []);

  // Check if already verified (runs after customerName is loaded)
  useEffect(() => {
    if (!customerName) return;
    // Prevent duplicate calls from React StrictMode or re-renders
    if (hasCheckedVerificationRef.current) return;
    hasCheckedVerificationRef.current = true;

    const checkExistingVerification = async () => {
      try{
        const response = await axios.get(`${API_BASE_URL}/api/caprion/digilocker/status/`);
        if (response.data.success && response.data.verified) {
          setVerificationComplete(true);
          setVerificationData({
            name: customerName,
            aadhaar_name: customerName,
            aadhaarNumber: response.data.aadhaar_number || '',
            panNumber: response.data.pan_number || '',
          });
          const faceMatchResponse = await axios.get(`${API_BASE_URL}/api/caprion/face-match/status/`);
          if (faceMatchResponse.data.success && faceMatchResponse.data.validated) {
            setFaceMatchValidated(true);
          } else {
            await validateFaceMatch();
          }
        }
      } catch { /* silent */ }
    };
    checkExistingVerification();
  }, [customerName]);

  // Register submit handler
  useEffect(() => {
    if (registerSubmit) registerSubmit(handleSubmit);
  }, [registerSubmit, verificationComplete, faceMatchValidated]);

  // Publish gate state to JourneyPage. aadhaar_verified is listed in this page's
  // layout.required_fields, so the footer Continue button stays greyed out until
  // both DigiLocker verification and the face match have succeeded.
  useEffect(() => {
    onChange?.({ aadhaar_verified: verificationComplete && faceMatchValidated });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationComplete, faceMatchValidated]);

  const validateFaceMatch = async () => {
    try {
      setFaceMatchLoading(true);
      setFaceMatchError('');
      const response = await axios.post(`${API_BASE_URL}/api/caprion/face-match/validate/`);
      if (response.data.success && response.data.matched) {
        setFaceMatchValidated(true);
      } else {
        setFaceMatchError(response.data.message || 'Face match validation failed');
      }
    } catch (err: any) {
      setFaceMatchError(err.response?.data?.message || 'Failed to validate face match');
    } finally {
      setFaceMatchLoading(false);
    }
  };

  const startPolling = (kid: string) => {
    // Prevent duplicate polling loops
    if (pollTimerRef.current) {
      console.log('[CaprionAadhaarVerification] Polling already active, skipping duplicate start');
      return;
    }

    setPollingActive(true);
    const maxAttempts = 150; // 150 attempts max (10 minutes if each takes ~4s)
    let attempts = 0;

    // Sequential polling: wait for response before next request
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setPollingActive(false);
        setError('Verification timeout. Please try again.');
        return;
      }

      attempts++;
      try {
        const statusResponse = await axios.post(
          `${API_BASE_URL}/api/caprion/digio/check-kyc-status/`,
          { kid }
        );
        
        if (statusResponse.data.success && statusResponse.data.status === 'verified') {
          // Success - stop polling
          if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
          }
          setPollingActive(false);
          setVerificationComplete(true);
          setVerificationData(statusResponse.data.data);
          if (onChange) onChange({ verified: true, kid, data: statusResponse.data.data });
          // Cleanup overlay once verification is done
          if (overlayCleanupRef.current) {
            overlayCleanupRef.current();
            overlayCleanupRef.current = null;
          }
          removeOrphanedDigioOverlays();
          await validateFaceMatch();
          if (onDone) onDone();
        } else {
          // Still pending - schedule next poll after 4 seconds
          pollTimerRef.current = setTimeout(poll, 4000);
        }
      } catch (err) {
        // Error - retry after 4 seconds
        console.warn('[CaprionAadhaarVerification] Poll error, retrying...', err);
        pollTimerRef.current = setTimeout(poll, 4000);
      }
    };

    // Start first poll immediately
    poll();
  };

  const initiateDigioKYC = async () => {
    try {
      setLoading(true);
      setError('');

      if (!sdkLoaded) {
        setError('Digio SDK is not loaded yet. Please wait.');
        setLoading(false);
        return;
      }

      const DigioCtor = getDigioConstructor();
      if (!DigioCtor) {
        setError('Digio SDK is not available. Please refresh and try again.');
        setLoading(false);
        return;
      }

      // Clean up any previous instance
      if (digioRef.current) {
        try { digioRef.current.cancel(); } catch { /* ignore */ }
        digioRef.current = null;
      }
      if (overlayCleanupRef.current) {
        overlayCleanupRef.current();
        overlayCleanupRef.current = null;
      }
      removeOrphanedDigioOverlays();

      const digio = new DigioCtor({
        environment: DIGIO_ENVIRONMENT,
        logo: `${window.location.origin}/logo.svg`,
        theme: { primaryColor: '#AB3498', secondaryColor: '#000000' },
        is_iframe: true,
      });

      try {
        digio.init();
        digioRef.current = digio;
        // Size the overlay to the visual viewport so it fits on mobile
        // (same pattern as NACH/UPI mandate components)
        overlayCleanupRef.current = fitDigioOverlayToViewport('DigiLocker KYC');
      } catch {
        setError('Failed to initialize verification window. Please check your browser settings.');
        removeOrphanedDigioOverlays();
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API_BASE_URL}/api/caprion/digio/initiate-kyc/`, {
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
      });

      if (!response.data.success) {
        setError(response.data.message || 'Failed to initiate KYC');
        if (overlayCleanupRef.current) { overlayCleanupRef.current(); overlayCleanupRef.current = null; }
        removeOrphanedDigioOverlays();
        setLoading(false);
        return;
      }

      const { kid, access_token } = response.data;
      if (!kid) {
        setError('Invalid response from server');
        if (overlayCleanupRef.current) { overlayCleanupRef.current(); overlayCleanupRef.current = null; }
        removeOrphanedDigioOverlays();
        setLoading(false);
        return;
      }

      // Extract the GWT token id from the access_token object (shape: {id: "GWT...", valid_till: "..."})
      // and resolve it the same way UPI/NACH mandates do — skips the Digio OTP/login screen.
      const tokenId = typeof access_token === 'object' && access_token !== null
        ? access_token.id
        : access_token;
      const validTill = typeof access_token === 'object' && access_token !== null
        ? access_token.valid_till
        : undefined;
      const resolvedToken = resolveDigioToken(tokenId, validTill, 'DigiLocker KYC');

      setLoading(false);
      digio.callback = () => startPolling(kid);

      try {
        digio.submit(kid, customerPhone.trim(), resolvedToken);
      } catch (popupError: any) {
        if (overlayCleanupRef.current) { overlayCleanupRef.current(); overlayCleanupRef.current = null; }
        removeOrphanedDigioOverlays();
        setError(
          popupError.message?.includes('popup') || popupError.message?.includes('blocked')
            ? 'Popup blocked! Please allow popups for this site and try again.'
            : 'Failed to open verification window. Please check your browser settings and try again.'
        );
        setLoading(false);
      }
    } catch (err: any) {
      if (overlayCleanupRef.current) { overlayCleanupRef.current(); overlayCleanupRef.current = null; }
      removeOrphanedDigioOverlays();
      setError(err.response?.data?.message || 'Failed to initiate KYC verification');
      setLoading(false);
    }
  };

  const handleSubmit = async (): Promise<boolean> => verificationComplete && faceMatchValidated;

  // ── Builder mode ─────────────────────────────────────────────────────────────
  if (isBuilderMode) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
        <div className="text-2xl mb-1">🛡️</div>
        <p className="font-semibold text-gray-500">Aadhaar Verification</p>
        <p className="text-xs mt-1">Initiates DigiLocker KYC via Digio SDK.</p>
      </div>
    );
  }

  // ── Verification complete ─────────────────────────────────────────────────────
  if (verificationComplete && verificationData) {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-intellend-green/30 bg-intellend-green/5 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-intellend-green/10 flex items-center justify-center shrink-0">
              <CheckCircle className="h-5 w-5 text-intellend-green" />
            </div>
            <div>
              <p className="font-bold text-gray-800">Aadhaar Verification Complete</p>
              <p className="text-xs text-intellend-green">Your identity has been verified successfully</p>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-intellend-green/20 px-4 py-3 space-y-1 text-sm">
            <p className="text-gray-700">
              <span className="font-semibold">Name:</span>{' '}
              {verificationData.aadhaar_name || verificationData.name || customerName || 'N/A'}
            </p>
            {(verificationData.aadhaar_number || verificationData.aadhaarNumber) && (
              <p className="text-gray-700">
                <span className="font-semibold">Aadhaar:</span>{' '}
                {verificationData.aadhaar_number || verificationData.aadhaarNumber}
              </p>
            )}
          </div>
        </div>

        {/* Face match status */}
        <div className={`rounded-xl border p-5 ${
          faceMatchValidated
            ? 'border-intellend-green/30 bg-intellend-green/5'
            : faceMatchError
            ? 'border-red-200 bg-red-50'
            : 'border-blue-200 bg-blue-50'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
              faceMatchValidated ? 'bg-intellend-green/10' : faceMatchError ? 'bg-red-100' : 'bg-blue-100'
            }`}>
              {faceMatchLoading ? (
                <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
              ) : faceMatchValidated ? (
                <CheckCircle className="h-5 w-5 text-intellend-green" />
              ) : faceMatchError ? (
                <AlertCircle className="h-5 w-5 text-red-600" />
              ) : (
                <Shield className="h-5 w-5 text-blue-600" />
              )}
            </div>
            <div>
              <p className={`font-bold ${
                faceMatchValidated ? 'text-gray-800' : faceMatchError ? 'text-red-700' : 'text-blue-700'
              }`}>
                {faceMatchLoading
                  ? 'Validating Face Match…'
                  : faceMatchValidated
                  ? 'Face Match Validated'
                  : faceMatchError
                  ? 'Face Match Failed'
                  : 'Validating Identity…'}
              </p>
              <p className={`text-xs ${
                faceMatchValidated ? 'text-intellend-green' : faceMatchError ? 'text-red-600' : 'text-blue-600'
              }`}>
                {faceMatchLoading
                  ? 'Comparing your selfie with Aadhaar photo…'
                  : faceMatchValidated
                  ? 'Your selfie matches your Aadhaar photo'
                  : faceMatchError || 'Please wait…'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Polling state ─────────────────────────────────────────────────────────────
  if (pollingActive) {
    return (
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-6 flex flex-col items-center text-center gap-4">
        <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
          <Loader2 className="h-7 w-7 text-blue-600 animate-spin" />
        </div>
        <div>
          <p className="font-bold text-blue-800 text-lg">Checking Verification Status</p>
          <p className="text-sm text-blue-600 mt-1">
            Please complete the verification in the popup window. We'll detect when you're done automatically.
          </p>
        </div>
      </div>
    );
  }

  // ── Initiation form ───────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-intellend-green/10 flex items-center justify-center shrink-0">
          <Shield className="h-5 w-5 text-intellend-green" />
        </div>
        <div>
          <p className="font-bold text-gray-800 text-lg leading-tight">Aadhaar Verification</p>
          <p className="text-xs text-gray-500">Verify your identity using DigiLocker</p>
        </div>
      </div>

      {/* Info box */}
      <div className="rounded-lg border border-intellend-green/20 bg-intellend-green/5 px-4 py-3 text-sm text-gray-700 leading-relaxed">
        We'll use DigiLocker to securely verify your Aadhaar details. This process is quick and secure.
      </div>

      {/* Errors */}
      {(error || sdkLoadError) && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error || sdkLoadError}</span>
        </div>
      )}

      {/* CTA */}
      <Button
        onClick={initiateDigioKYC}
        disabled={loading || !sdkLoaded}
        className="w-full h-12 text-base font-bold bg-intellend-green hover:bg-intellend-green/90 shadow-md transition-all active:scale-[0.98]"
      >
        {loading ? (
          <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Initiating…</>
        ) : !sdkLoaded ? (
          'Loading SDK…'
        ) : (
          'Start Verification'
        )}
      </Button>
    </div>
  );
};

export default CaprionAadhaarVerification;
